import React, {useState, useEffect} from "react";
import SoftBox from "../../../components/SoftBox";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import voicesData from "./../../../constants/voices.json";
import VoiceOptionsSelection from "./VoiceOptionsSelection";
import {useSoftUIController} from "../../../context";
import {useTranslation} from "react-i18next";

const Services = ({
                      selectedLanguage,
                      label,
                      services,
                      handleCheckboxServiceChange,
                      selectedServices,
                      formData,
                      setFormData,
                      errors,
                      setErrors
                  }) => {
    const maleArabicVoices = voicesData.voices.arabic.male;
    const aliVoice = maleArabicVoices.find(voice => voice.name === "Ali");

    const [showVoiceSelection, setShowVoiceSelection] = useState(false);
    const [selectedVoice, setSelectedVoice] = useState({url: "", voice_name: ""});
    const [selectedAudioFormat, setSelectedAudioFormat] = useState("");
    const [currentAudio, setCurrentAudio] = useState(null);
    const [playVoice, setPlayVoice] = useState(null);
    const supportedLanguages = [
        'english', 'الإنجليزية',
        'arabic', 'العربية',
        'spanish', 'الإسبانية',
        'french', 'الفرنسية',
        'chinese', 'الصينية',
        'german', 'الألمانية',
        'turkish', 'التركية',
        'japanese', 'اليابانية',
        'russian', 'الروسية'
    ];
    const [controller, dispatch] = useSoftUIController();
    const { t, i18n } = useTranslation("publicationsTranslation");

    useEffect(() => {

        if (currentAudio) {
            currentAudio.addEventListener('ended', handleAudioEnd);
        }

        return () => {

            if (currentAudio) {
                currentAudio.pause();
                currentAudio.currentTime = 0;

                currentAudio.removeEventListener('ended', handleAudioEnd);
                currentAudio.pause();
                currentAudio.currentTime = 0;
            }
        };
    }, [currentAudio]);

    useEffect(() => {
        const audioBookService = services.find(
            service => service.name === 'Text to Speech' || service.name === "ملف صوتي"
        );
        if (!selectedServices?.includes(audioBookService?.id)) {
            setShowVoiceSelection(null)
            setSelectedVoice({url: "", voice_name: ""})
            updateVoiceName('')
            setFormData(prevState => ({
                ...prevState,
                audioFormat: '',
                voiceName: "",
            }));
        } else {
            if(!formData?.voiceName){
                setShowVoiceSelection(true)
                setSelectedVoice(aliVoice)
                setFormData(prevState => ({
                    ...prevState,
                    audioFormat: 'wav',
                    voiceName: (aliVoice.voice_name),
                }));
                setSelectedAudioFormat('wav')
            }
        }

        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setPlayVoice(null)
        }

    }, [selectedServices]);

    useEffect(() => {
        setShowVoiceSelection(false)
        setSelectedVoice({url: "", voice_name: ""})
        updateVoiceName('')
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setPlayVoice(null)
        }

        const audioBookService = services.find(
            service => service.name === 'Text to Speech' || service.name === "ملف صوتي"
        );
        if (audioBookService) {
            setFormData(prevState => ({
                ...prevState,
                services: selectedServices.filter(serviceId => serviceId !== audioBookService.id),
                audioFormat: '',
                voiceName: '',
            }));
        }

        setErrors(prevErrors => ({
            ...prevErrors,
            audioFormat: '',
            voiceName: ''
        }));

        setSelectedAudioFormat('')

    }, [selectedLanguage]);

    const handleAudioEnd = () => {
        setPlayVoice(null);
    };

    const updateVoiceName = (voiceName) => {
        setFormData(prevState => ({
            ...prevState,
            voiceName: voiceName
        }));
        setErrors(prevErrors => ({...prevErrors, voiceName: ''}));
    }

    const updateAudioFormat = (audioFormat) => {
        setFormData(prevState => ({
            ...prevState,
            audioFormat: audioFormat
        }));
        setErrors(prevErrors => ({...prevErrors, audioFormat: ''}));
    }

    const handleCheckboxChange = (e, serviceId, serviceName) => {
        handleCheckboxServiceChange(e, serviceId);
        if ((serviceName === "Text to Speech" || serviceName === "ملف صوتي") && !e.target.checked){
            setErrors(prevErrors => ({
                ...prevErrors,
                audioFormat: '',
                voiceName: ''
            }));
        }
    };

    const handleAudioFormatChange = (event) => {
        setSelectedAudioFormat(event.target.value);
        updateAudioFormat(event.target.value)
    };

    const handlePlayVoice = (voiceUrl) => {
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
            setPlayVoice(null)
        }

        if (voiceUrl !== playVoice) {
            const newAudio = new Audio(voiceUrl);
            setCurrentAudio(newAudio);
            setPlayVoice(voiceUrl)

            newAudio.play().catch((error) => {
                if (error.name === 'AbortError') {
                    console.log('Play was interrupted by another pause or play action.');
                } else {
                    console.error('Error playing audio:', error);
                }
            });

        }

    };

    const voicesByLanguage = voicesData?.voices?.[selectedLanguage] || {};
    const femaleVoices = voicesByLanguage?.female || [];
    const maleVoices = voicesByLanguage?.male || [];

    return (
        <React.Fragment>
            <label htmlFor="subjectTerms" className="label-style">
                {label}
            </label>
            <SoftBox style={{display: "grid", gridTemplateColumns: "50% 50%", gap:"0.6rem"}}>
                {services.map((service, index) => {
                    if ((service.name === 'Text to Speech' || service.name === "ملف صوتي") && !supportedLanguages.includes(selectedLanguage)) {
                        return null;
                    }

                    if ((service.name === 'Diacritics' || service.name === 'التشكيل') && selectedLanguage !== "arabic" && selectedLanguage !== "العربية") {
                        return null;
                    }

                    return (
                        <div key={index} style={{display: "flex", alignItems: "center"}}>
                            <FormControlLabel
                                key={index}
                                style={{margin: "0px"}}
                                control={
                                    <Checkbox
                                        checked={selectedServices.includes(service.id)}
                                        onChange={(e) => handleCheckboxChange(e, service.id, service.name)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <span style={{fontWeight: 400}}>
                                        {service.name}
                                    </span>
                                }
                                sx={{
                                    margin: 0,
                                    ...(i18n.dir() === "rtl"
                                        ? { '& .MuiFormControlLabel-label': { marginRight: "8px" } }
                                        : { '& .MuiFormControlLabel-label': { marginLeft: "8px" } })
                                }}
                                labelPlacement={i18n.dir() === "rtl" ? "start" : "end"}
                            />
                        </div>
                            );
                                })}
                            </SoftBox>

            {
                showVoiceSelection && (
                    <>
                        <div style={{display: "flex", marginTop: "20px", marginLeft: '10px'}}>
                            <VoiceOptionsSelection
                                voices={femaleVoices}
                                selectedVoice={selectedVoice}
                                setSelectedVoice={setSelectedVoice}
                                playVoice={playVoice}
                                handlePlayVoice={handlePlayVoice}
                                label={t("selectFemaleVoice")}
                                updateVoiceName={updateVoiceName}
                            />
                            <VoiceOptionsSelection
                                voices={maleVoices}
                                selectedVoice={selectedVoice}
                                setSelectedVoice={setSelectedVoice}
                                playVoice={playVoice}
                                handlePlayVoice={handlePlayVoice}
                                label={t("selectMaleVoice")}
                                updateVoiceName={updateVoiceName}
                                style={{marginLeft: "130px"}}
                            />
                        </div>
                    {errors.voiceName && <p className="error-text">{errors.voiceName}</p>}

                    <label className="label-style">{t("selectAudioFormat")}</label>
                    <Select value={selectedAudioFormat} onChange={handleAudioFormatChange}>
                        <MenuItem value="wav">WAV</MenuItem>
                        <MenuItem value="mp3">MP3</MenuItem>
                    </Select>
                    {errors.audioFormat && <p className="error-text">{errors.audioFormat}</p>}
                </>
            )}
        </React.Fragment>
    );
};

Services.propTypes = {
    label: PropTypes.string,
    selectedLanguage: PropTypes.string,
    services: PropTypes.array,
    formData: PropTypes.array,
    selectedServices: PropTypes.array,
    errors: PropTypes.object,
    setErrors: PropTypes.func,
    setFormData: PropTypes.func,
    handleCheckboxServiceChange: PropTypes.func,
};

export default Services;